import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { Button } from '@cropler/ui-kit';
import * as turf from '@turf/turf';

// import { Capacitor } from '@capacitor/core';
// import { Geolocation } from '@capacitor/geolocation';

import styles from './styles.module.scss';
import { ReactComponent as NavigateIcon } from './assets/icons/navigate.svg';

import { mapBoxId, mapBoxStyles } from '../../constants/keys';
import { TileMinZoom } from '../Map/constants/index.jsx';
import { PathnamesMap } from '../../constants/ui';
import { SidebarItemInner } from '../Sidebar/index.jsx';
import { NavigatorTargetMarker } from './index.jsx';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

mapboxgl.accessToken = mapBoxId;

const Navigator = ({ t }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [distanceToTarget, setDistanceToTarget] = useState(null);

  const map = useRef(null);
  const mapContainer = useRef(null);

  const geolocateControl = useRef(null);
  const markerTargetRef = useRef(null);
  const userPositionRef = useRef(null);
  const isTrackingRef = useRef(false);
  const fullRouteCoordsRef = useRef([]);
  const shouldFitBoundsRef = useRef(true);
  const permissionRequestedRef = useRef(false);
  const geolocationRequestedRef = useRef(false);

  const goToMap = () => {
    navigate(`/${PathnamesMap.map}`);
  };

  // Clear target marker
  const clearTargetMarker = () => {
    if (markerTargetRef.current) {
      markerTargetRef.current.remove();
      markerTargetRef.current = null;
    }
  };

  // Show the full route
  const fitRouteInView = () => {
    if (!map.current || !fullRouteCoordsRef.current.length) return;

    const bounds = new mapboxgl.LngLatBounds();
    fullRouteCoordsRef.current.forEach((coord) => bounds.extend(coord));

    map.current.fitBounds(bounds, {
      padding: { top: 100, bottom: 100, left: 50, right: 50 },
      maxZoom: 16,
      duration: 1000, // Animation
    });
  };

  // Click Re-center
  const handleRecenter = async () => {
    if (!permissionRequestedRef.current) {
      permissionRequestedRef.current = true;
      await requestDeviceOrientation();
    }
    if (!geolocationRequestedRef.current) {
      geolocationRequestedRef.current = true;
      geolocateControl.current?.trigger();
    }
    shouldFitBoundsRef.current = true;
    fitRouteInView();
  };

  // Updating the route as the user moves
  const updateRoute = async (userCoords, targetCoords) => {
    // Distance to target
    const from = turf.point(userCoords);
    const to = turf.point(targetCoords);
    const distance = turf.distance(from, to, { units: 'kilometers' });
    setDistanceToTarget(distance.toFixed(2));

    // Requesting directions from Mapbox Directions API
    const url = `https://api.mapbox.com/directions/v5/mapbox/walking/${userCoords[0]},${userCoords[1]};${targetCoords[0]},${targetCoords[1]}?steps=true&geometries=geojson&access_token=${mapboxgl.accessToken}`;

    let fullRouteCoords = [];
    try {
      const query = await fetch(url);
      const data = await query.json();

      if (data.routes && data.routes.length > 0 && data.routes[0].geometry) {
        const roadRoute = data.routes[0].geometry.coordinates;
        fullRouteCoords = [...roadRoute];

        // Дорисовываем линию от конца маршрута до точки назначения
        const lastRoadPoint = roadRoute[roadRoute.length - 1];
        if (lastRoadPoint[0] !== targetCoords[0] || lastRoadPoint[1] !== targetCoords[1]) {
          fullRouteCoords.push(targetCoords);
        }
      } else {
        console.warn('Mapbox directions not found, drawing straight line.');
        fullRouteCoords = [userCoords, targetCoords];
      }
    } catch (error) {
      console.error('Error fetching route:', error);
      fullRouteCoords = [userCoords, targetCoords];
    }

    if (!map.current) return;

    // Save coords
    fullRouteCoordsRef.current = fullRouteCoords;

    // Update/add route
    if (map.current.getSource('route')) {
      map.current.getSource('route').setData({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: fullRouteCoords,
        },
      });
    } else {
      map.current.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: fullRouteCoords,
          },
        },
      });

      // Add the route line
      map.current.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        paint: {
          'line-width': 3,
          'line-color': '#3b82f6',
        },
      });
    }

    // Show entire route only if flag is set
    if (shouldFitBoundsRef.current) {
      fitRouteInView();
      shouldFitBoundsRef.current = false;
    }
  };

  const requestDeviceOrientation = async () => {
    // Request access to device sensors (iOS 13+)
    if (
      typeof DeviceOrientationEvent !== 'undefined' &&
      typeof DeviceOrientationEvent.requestPermission === 'function'
    ) {
      try {
        const permission = await DeviceOrientationEvent.requestPermission();
        if (permission === 'granted') {
          console.log('Device orientation permission granted');
        } else {
          console.warn('Device orientation permission denied');
        }
      } catch (error) {
        console.error('Error requesting device orientation permission:', error);
      }
    }
    // For native apps (iOS/Android) - request geolocation permission via Capacitor
    // if (Capacitor.isNativePlatform()) {
    //   try {
    //     const permission = await Geolocation.checkPermissions();
    //
    //     if (permission.location !== 'granted') {
    //       const requested = await Geolocation.requestPermissions();
    //
    //       if (requested.location === 'granted') {
    //         console.log('Geolocation permission granted');
    //       } else {
    //         console.warn('Geolocation permission denied');
    //       }
    //     }
    //   } catch (error) {
    //     console.error('Error requesting geolocation permission:', error);
    //   }
    // }
  };

  // Mapbox init (only once)
  useEffect(() => {
    const lat = Number(searchParams.get('lat'));
    const lng = Number(searchParams.get('lng'));

    if (map.current || !lat || !lng) return;

    // Mapbox init
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapBoxStyles,
      center: [lng, lat],
      zoom: TileMinZoom,
      hash: false,
      pitch: 0, // Force-enable pitch for iOS (needed for the directional arrow)
    });

    // Geolocation control (marker + directional arrow)
    geolocateControl.current = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000,
      },
      trackUserLocation: true, // Motion tracking
      showUserHeading: true, // Show direction (arrow)
      showAccuracyCircle: true,
      fitBoundsOptions: {
        maxZoom: 16,
      },
    });

    map.current.addControl(geolocateControl.current);
    map.current.addControl(new mapboxgl.NavigationControl());

    map.current.on('load', async () => {
      // Custom target marker
      const targetMarkerEl = document.createElement('div');
      const targetMarkerRoot = createRoot(targetMarkerEl);
      targetMarkerRoot.render(<NavigatorTargetMarker />);
      markerTargetRef.current = new mapboxgl.Marker({
        element: targetMarkerEl,
        anchor: 'center',
      })
        .setLngLat([lng, lat])
        .addTo(map.current);

      // Requesting access to the device's sensors
      await requestDeviceOrientation();

      // Start geolocate
      geolocateControl.current.trigger();
    });

    // Start tracking
    geolocateControl.current.on('trackuserlocationstart', () => {
      isTrackingRef.current = true;
    });

    // End tracking
    geolocateControl.current.on('trackuserlocationend', () => {
      isTrackingRef.current = false;
    });

    // User position received/updated
    geolocateControl.current.on('geolocate', async (e) => {
      const userCoords = [e.coords.longitude, e.coords.latitude];
      userPositionRef.current = userCoords;
      await updateRoute(userCoords, [lng, lat]);
    });

    // Geolocate error
    geolocateControl.current.on('error', (error) => {
      console.error('Geolocation error:', error);
    });

    // Track only manual user interaction with the map
    let userInteracting = false;

    const handleTouchStart = () => {
      userInteracting = true;
    };

    const handleTouchEnd = () => {
      setTimeout(() => {
        userInteracting = false;
      }, 100);
    };

    const handleMoveStart = () => {
      if (userInteracting) {
        // The user started moving the map manually - disable autofit
        shouldFitBoundsRef.current = false;
      }
    };

    // Listening for touch events (mobile)
    mapContainer.current.addEventListener('touchstart', handleTouchStart);
    mapContainer.current.addEventListener('touchend', handleTouchEnd);

    // Listening for mouse events (desktop)
    mapContainer.current.addEventListener('mousedown', handleTouchStart);
    mapContainer.current.addEventListener('mouseup', handleTouchEnd);

    // Listen to the movement of the map
    map.current.on('movestart', handleMoveStart);

    return () => {
      clearTargetMarker();
      if (geolocateControl.current) {
        geolocateControl.current.off('geolocate');
        geolocateControl.current.off('error');
        geolocateControl.current.off('trackuserlocationstart');
        geolocateControl.current.off('trackuserlocationend');
      }
      if (map.current) {
        map.current.off('movestart');
        map.current.remove();
        map.current = null;
      }
    };
  }, [searchParams]);

  return (
    <div className={styles.navigator}>
      {/* Header */}
      <div className={styles.navigatorHeader}>
        <span>{t('navigator.title')}</span>
        <Button
          className={styles.navigatorClose}
          type="gray-filled"
          size={'small'}
          icon={<CroplerIcon name="close" />}
          onClick={() => goToMap()}
        />
      </div>
      {searchParams.get('lat') && searchParams.get('lng') ? (
        <>
          {/* Map */}
          <div ref={mapContainer} className={`${styles.navigatorMap} map-navigator`} />

          {/* Re-center button */}
          <Button
            className={styles.navigatorReCenter}
            type="primary"
            size={'large'}
            icon={<NavigateIcon />}
            onClick={handleRecenter}
          >
            {t('navigator.reCenter')}
          </Button>

          {/* Info panel */}
          <div className={styles.navigatorInfo}>
            <div className={styles.navigatorInfoTitle}>
              <SidebarItemInner title={searchParams.get('name') || t('labels.sensor') || 'Sensor'} />
            </div>
            <>{distanceToTarget ? <div>{distanceToTarget} km</div> : <div>{t('navigator.calculating')}...</div>}</>
          </div>
        </>
      ) : (
        // Empty view
        <div className={styles.navigatorEmpty}>
          {t('navigator.noSensorSelected')}
          <Button type="primary" size={'large'} icon={<CroplerIcon name="maps" />} onClick={goToMap}>
            {t('navigator.returnToMap')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Navigator;
